import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
} from "../components/ui";

export interface PostRow {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  status: "draft" | "published";
  readingMinutes: number;
  publishedAt: string | null;
  updatedAt: string;
  cover: { id: string; alt: string | null; filename: string } | null;
  author: { id: string; email: string } | null;
}

/**
 * The writing desk.
 *
 * Ordered by when it was last touched rather than by publication date, because
 * this is a list of work in progress — the post you were editing an hour ago is
 * the one you came back for, whatever its status.
 *
 * "New post" is a link, not a form. It used to ask for a title here, create the
 * row, and leave you on this page to go and find it; every abandoned attempt
 * left an empty draft in the table. Nothing is written until the editor saves.
 */
export function Blog() {
  const posts = useQuery({
    queryKey: ["posts"],
    queryFn: () => api<PostRow[]>("/api/v1/admin/posts"),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Blog"
        lede="Posts are written here and read by the website. Nothing appears publicly until it is published."
      />

      <Card
        title="Posts"
        action={
          <Link to="/blog/new">
            <Button variant="primary">New post</Button>
          </Link>
        }
      >
        {posts.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : posts.isError ? (
          <ErrorNote>Could not load the posts.</ErrorNote>
        ) : (posts.data?.length ?? 0) === 0 ? (
          <EmptyState>Nothing written yet. A new post starts as a draft.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Title</Th>
                  <Th>Status</Th>
                  <Th>Category</Th>
                  <Th>Read</Th>
                  <Th>Published</Th>
                  <Th>Last edited</Th>
                </tr>
              </thead>
              <tbody>
                {(posts.data ?? []).map((post) => (
                  <tr key={post.id} className="border-b border-hairline last:border-0">
                    <Td label="Title">
                      <Link
                        to={`/blog/${post.id}`}
                        className="text-accent underline-offset-4 hover:underline"
                      >
                        {post.title === "" ? "Untitled" : post.title}
                      </Link>
                      <span className="block text-xs text-dim tabular">/{post.slug}</span>
                    </Td>
                    <Td label="Status">
                      <Badge tone={post.status === "published" ? "live" : "quiet"}>
                        {post.status === "published" ? "Published" : "Draft"}
                      </Badge>
                    </Td>
                    <Td label="Category" className="text-muted">
                      {post.category}
                    </Td>
                    <Td label="Read" className="tabular text-muted">
                      {post.readingMinutes} min
                    </Td>
                    <Td label="Published" className="text-muted">
                      {post.publishedAt === null ? (
                        <span className="text-dim">—</span>
                      ) : (
                        formatDate(post.publishedAt)
                      )}
                    </Td>
                    <Td label="Last edited" className="text-muted">
                      {formatDate(post.updatedAt)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
